import { Button } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import type { FilterState } from '@/components/filter-bar';
import { FilterEditDialog } from '@/components/filter-bar';
import { StatusChip } from '@/components/ui/status-chip';
import { type AbuseDashboardState, customToRange, presetToRange, RANGE_PRESET_OPTIONS, rangeProblem, readDashboardState, toDashboardParams } from '@/features/abuse/abuse-filters';
import { EventsCard } from '@/features/abuse/events/events-card';
import { BreakdownCards } from '@/features/abuse/overview/breakdown-cards';
import { SummaryTiles } from '@/features/abuse/overview/summary-tiles';
import { TrendCard } from '@/features/abuse/overview/trend-chart';
import { TopSubjectsCard } from '@/features/abuse/top-subjects/top-subjects-card';
import { ABUSE_QUERY_KEY, useAbuseSummary } from '@/hooks/queries/compliance/use-abuse';
import { getErrorMessage } from '@/lib/api/error';

export const AbusePanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const state = readDashboardState(searchParams);
  const [isCustomRangeOpen, setCustomRangeOpen] = useState(false);
  // Bumped by Refresh so a page left open past midnight rolls its window onto the new UTC day
  const [refreshedAt, setRefreshedAt] = useState(() => Date.now());
  const { preset, customDates } = state;
  const [customFrom, customTo] = customDates;
  // Frozen per selection, not per render, so query keys and the event cursor walk stay stable
  const range = useMemo(
    () => (preset === 'custom' ? customToRange([customFrom, customTo], new Date(refreshedAt)) : presetToRange(preset, new Date(refreshedAt))),
    [customFrom, customTo, preset, refreshedAt],
  );
  const problem = rangeProblem(range);
  const summaryQuery = useAbuseSummary(range, problem === null);
  const summary = summaryQuery.data;
  const thresholds = useMemo(() => (summary ? { threshold: summary.scoring.threshold, escalationThreshold: summary.scoring.escalationThreshold } : null), [summary]);
  const services = useMemo(() => summary?.byService.map((row) => row.service) ?? [], [summary]);
  const eventFiltersKey = JSON.stringify(state.eventFilters);

  // Replace, not push: selections should not fill the history that Back walks through
  const update = (patch: Partial<AbuseDashboardState>) => setSearchParams(toDashboardParams({ ...state, ...patch }), { replace: true });

  // Same-day windows keep the same query keys, so refreshing means invalidating rather than moving the window
  const handleRefresh = () => {
    setRefreshedAt(Date.now());
    queryClient.invalidateQueries({ queryKey: ABUSE_QUERY_KEY });
  };

  const handleCustomRangeApply = (values: string[]) => {
    setCustomRangeOpen(false);
    update(values.some(Boolean) ? { preset: 'custom', customDates: values } : { preset: '30d', customDates: [] });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <fieldset aria-label="Time range" className="m-0 flex items-center gap-1 border-0 p-0">
            {RANGE_PRESET_OPTIONS.map((option) => (
              <Button
                key={option.value}
                onClick={() => update({ preset: option.value, customDates: [] })}
                size="sm"
                type="button"
                variant={preset === option.value ? 'primary' : 'outline'}
              >
                {option.label}
              </Button>
            ))}
            <Button onClick={() => setCustomRangeOpen(true)} size="sm" type="button" variant={preset === 'custom' ? 'primary' : 'outline'}>
              Custom…
            </Button>
          </fieldset>
          <Button aria-label="Refresh" onClick={handleRefresh} size="sm" title="Refresh" type="button" variant="ghost">
            <RotateCw className="size-4" />
          </Button>
        </div>
        {summary ? (
          <StatusChip variant={summary.mode === 'live' ? 'destructive' : 'info'}>
            {summary.mode === 'live' ? 'Live: crossings open compliance cases' : 'Shadow mode: crossings are recorded, no cases opened'}
          </StatusChip>
        ) : null}
      </div>
      {isCustomRangeOpen ? (
        <FilterEditDialog initial={customDates} onApply={handleCustomRangeApply} onClose={() => setCustomRangeOpen(false)} options={[]} title="Date range" type="date" />
      ) : null}
      {problem ? (
        <QueryErrorBanner message={problem} />
      ) : (
        <>
          {summaryQuery.isError ? (
            <QueryErrorBanner message={`Failed to fetch the abuse summary: ${getErrorMessage(summaryQuery.error)}`} onRetry={() => summaryQuery.refetch()} />
          ) : (
            <>
              <SummaryTiles isLoading={summaryQuery.isPending} summary={summary} />
              <TrendCard isLoading={summaryQuery.isPending} summary={summary} />
              <BreakdownCards isLoading={summaryQuery.isPending} summary={summary} />
            </>
          )}
          <TopSubjectsCard
            onSortChange={(subjectsSort) => update({ subjectsSort })}
            onSubjectTypeChange={(subjectsType) => update({ subjectsType })}
            since={range.from}
            sort={state.subjectsSort}
            subjectType={state.subjectsType}
            thresholds={thresholds}
          />
          <EventsCard
            filters={state.eventFilters}
            key={`${range.from}|${range.to}|${state.feedType}|${eventFiltersKey}`}
            onFiltersChange={(eventFilters: FilterState) => update({ eventFilters })}
            onSubjectTypeChange={(feedType) => update({ feedType })}
            range={range}
            services={services}
            subjectType={state.feedType}
          />
        </>
      )}
    </div>
  );
};
AbusePanel.displayName = 'AbusePanel';
